'use client';

import { useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { discountCouponsApi } from '@/lib/api/discount-coupons';
import { pricingModal } from '@/lib/stores/pricing-modal-store';
import { useAuthStore } from '@/stores/authStore';
import type { DiscountCouponClaim } from '@/types/discount-coupons';

const CLAIMED_COUPON_STORAGE_KEY = 'dobidobi:claimed-coupon';

function isClaimSelectableForTopup(claim: DiscountCouponClaim | null | undefined): claim is DiscountCouponClaim {
  if (claim?.status !== 'claimed' || !claim.coupon) return false;
  return claim.coupon.public_status === 'active' || claim.coupon.public_status === 'claimed_out';
}

function readStashedClaim(userId: string | null) {
  if (typeof window === 'undefined') return null;
  let rawClaim: string | null = null;
  try {
    rawClaim = sessionStorage.getItem(CLAIMED_COUPON_STORAGE_KEY);
    sessionStorage.removeItem(CLAIMED_COUPON_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!rawClaim) return null;
  try {
    const parsed = JSON.parse(rawClaim) as { userId?: unknown; claim?: unknown };
    return parsed.userId === userId && parsed.claim && typeof parsed.claim === 'object'
      ? parsed.claim as DiscountCouponClaim
      : null;
  } catch {
    return null;
  }
}

export function CouponClaimExploreHandler() {
  const router = useRouter();
  const searchParams = useSearchParams()!;
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const handledRef = useRef(false);

  useEffect(() => {
    if (searchParams.get('coupon_claim') !== '1') return;
    if (!userId) return;
    if (handledRef.current) return;
    handledRef.current = true;

    let mounted = true;
    const stashedClaim = readStashedClaim(userId);

    const openTopupModal = (claim: DiscountCouponClaim | null) => {
      if (!mounted) return;
      pricingModal.open({
        triggerReason: 'manual',
        defaultTab: 'topup',
        couponClaim: claim,
      });
      router.replace('/explore', { scroll: false });
    };

    if (isClaimSelectableForTopup(stashedClaim)) {
      openTopupModal(stashedClaim);
      return () => {
        mounted = false;
      };
    }

    discountCouponsApi.listMyAvailableClaims()
      .then((response) => {
        const claim = response.data?.claims?.[0] ?? null;
        openTopupModal(claim);
      })
      .catch(() => {
        openTopupModal(null);
      });

    return () => {
      mounted = false;
    };
  }, [router, searchParams, userId]);

  return null;
}
